import type { AutoTopup, CreditPricing, CreditState, CreditUnit, InsufficientCreditsError, TopUp } from "api";
import { formatDate, formatShortDate, formatTimestamp } from "utils/date.utils";
import { formatCount } from "utils/number.utils";

/**
 * The slider is not linear in credits: equal steps of travel land on the packs
 * the design names (10, 75, 250, 650) and end at 1600. Between two anchors it
 * is linear. Anything above the last anchor goes through "Any amount".
 */
export const SLIDER_ANCHORS: readonly (readonly [position: number, credits: number])[] = [
  [0, 1],
  [200, 10],
  [400, 75],
  [600, 250],
  [800, 650],
  [1000, 1600],
];

export const SLIDER_MAX = 1000;

/** The credits at the end of the slider; the number field takes over above it. */
export const SLIDER_TOP = SLIDER_ANCHORS[SLIDER_ANCHORS.length - 1][1];

/** Where the calculator opens: the "Small teams" pack. */
export const DEFAULT_CREDITS = 75;

/** Above this a month is a sales conversation, not a pack (design copy). */
export const CONTACT_THRESHOLD = 3000;

export const QUOTE_URL = "/request-it-quote";

export const toCredits = (position: number): number => {
  for (let index = 1; index < SLIDER_ANCHORS.length; index += 1) {
    const [toPosition, toCreditsValue] = SLIDER_ANCHORS[index];

    if (position <= toPosition) {
      const [fromPosition, fromCredits] = SLIDER_ANCHORS[index - 1];

      return Math.round(
        fromCredits + ((toCreditsValue - fromCredits) * (position - fromPosition)) / (toPosition - fromPosition)
      );
    }
  }

  return SLIDER_TOP;
};

export const toPosition = (credits: number): number => {
  const capped = Math.min(SLIDER_TOP, credits);

  for (let index = 1; index < SLIDER_ANCHORS.length; index += 1) {
    const [toPositionValue, toCreditsValue] = SLIDER_ANCHORS[index];

    if (capped <= toCreditsValue) {
      const [fromPosition, fromCredits] = SLIDER_ANCHORS[index - 1];

      return (
        fromPosition + ((toPositionValue - fromPosition) * (capped - fromCredits)) / (toCreditsValue - fromCredits)
      );
    }
  }

  return SLIDER_MAX;
};

export const clampCredits = (value: number, pricing: Pick<CreditPricing, "min_credits" | "max_credits">): number =>
  Math.max(pricing.min_credits, Math.min(pricing.max_credits, Math.round(Number.isFinite(value) ? value : 0)));

/** The rate of the highest tier the amount reaches. The whole amount is priced at it. */
export const rateFor = (pricing: Pick<CreditPricing, "tiers">, credits: number): number => {
  let rate = pricing.tiers[0]?.unit_price ?? 0;

  pricing.tiers.forEach((tier) => {
    if (credits >= tier.min_quantity) rate = tier.unit_price;
  });

  return rate;
};

/**
 * Whole dollars, as the back office charges: Math.round(n * rate - 0.001), so
 * an exact half rounds down (650 × 0.77 = 500.50 → 500). Worked in
 * micro-dollars to keep 0.625 exact.
 */
export const totalUsd = (pricing: Pick<CreditPricing, "tiers">, credits: number): number => {
  const micros = Math.round(rateFor(pricing, credits) * 1_000_000);

  return Math.floor((credits * micros + 499_000) / 1_000_000);
};

/** "−47%" against the base rate, or null when the amount is at the base rate. */
export const discountPercent = (pricing: Pick<CreditPricing, "tiers">, credits: number): number | null => {
  const base = pricing.tiers[0]?.unit_price ?? 0;
  const rate = rateFor(pricing, credits);
  const percent = base > 0 ? Math.round((1 - rate / base) * 100) : 0;

  return rate < base && percent > 0 ? percent : null;
};

/** "USD 1.33", or three decimals when the third one counts: "USD 0.625". */
export const formatRate = (rate: number): string =>
  `USD ${Math.round(rate * 1000) % 10 !== 0 ? rate.toFixed(3) : rate.toFixed(2)}`;

export const formatUsd = (amount: number): string => `USD ${formatCount(amount)}`;

/** "USD 100.00" from Stripe's minor units. */
export const formatInvoiceMoney = (minor: number, currency: string): string =>
  `${currency.toUpperCase()} ${(minor / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatCredits = (credits: number): string =>
  `${formatCount(credits)} ${Math.abs(credits) === 1 ? "QCredit" : "QCredits"}`;

/** "3 shipments", "1 search". */
export const formatUnits = (count: number, unit: CreditUnit | null): string => {
  const word = unit ?? "use";
  const plural = word === "search" ? "searches" : `${word}s`;

  return `${formatCount(count)} ${count === 1 ? word : plural}`;
};

/** "2 QCredits / 1 shipment", as the price list prints it. */
export const formatCost = (credits: number, unit: CreditUnit): { credits: string; per: string } => ({
  credits: formatCredits(credits),
  per: `/ 1 ${unit}`,
});

/**
 * What the pane shows, in the design's own states:
 * - `empty`: nothing bought yet and no auto top-up: the free credits and the price list;
 * - `filled`: a balance, its activity and invoices, and the auto top-up card if there is one.
 */
export type BillingView = {
  empty: boolean;
  auto: AutoTopupView | null;
};

export type AutoTopupView = {
  /** `ending`: turned off, but still paid through the current month. */
  status: "active" | "failed" | "ending";
  topUp: AutoTopup;
};

export const billingView = (state: CreditState): BillingView => {
  const auto = state.auto_top_up;
  const autoView: AutoTopupView | null =
    auto === null
      ? null
      : {
          status: auto.cancel_at_period_end ? "ending" : auto.status === "failed" ? "failed" : "active",
          topUp: auto,
        };

  return {
    empty: autoView === null && state.last_top_up === null && state.balance.paid === 0,
    auto: autoView,
  };
};

const topUpLine = (topUp: TopUp): string =>
  `Last top-up: ${formatCredits(topUp.credits)} on ${formatTimestamp(topUp.at)}`;

/** The line under the balance: free credits, the last top-up, or the auto top-up. */
export const balanceLine = (state: CreditState, view: BillingView): string => {
  const { balance } = state;

  if (view.empty) {
    return `${formatCount(balance.free_monthly)} free QCredits every month · next top-up on ${formatDate(balance.free_resets_on)}`;
  }

  if (view.auto !== null) {
    const base = `Auto top-up: ${formatCredits(view.auto.topUp.credits)} monthly`;

    if (view.auto.status === "failed") return `${base} · payment failed`;
    if (view.auto.status === "ending")
      return `${base} · ends on ${formatTimestamp(view.auto.topUp.current_period_end)}`;

    return `${base} · next on ${formatTimestamp(view.auto.topUp.next_charge_at)}`;
  }

  if (state.last_top_up !== null) return topUpLine(state.last_top_up);

  return `${formatCount(balance.free)} free QCredits left this month`;
};

/** "Visa •• 4242". */
export const cardLabel = (card: AutoTopup["card"]): string | null => {
  if (card === null) return null;

  const brand = card.brand === null ? "Card" : card.brand.charAt(0).toUpperCase() + card.brand.slice(1);

  return `${brand} •• ${card.last4}`;
};

export type BuyMode = "once" | "extra" | "change";

/** Heading, subtitle and button of the buy card in each of its modes. */
export const buyCopy = (mode: BuyMode, auto: AutoTopup | null): { title: string; sub: string; cta: string } => {
  if (mode === "extra") {
    return {
      title: "Buy extra QCredits",
      sub:
        auto === null
          ? "One-time purchase. Credits never expire."
          : `One-time purchase. Your auto top-up stays ${formatCredits(auto.credits)} monthly.`,
      cta: "Buy QCredits",
    };
  }

  if (mode === "change") {
    const next = auto?.next_charge_at ?? auto?.current_period_end ?? null;

    return {
      title: "Change auto top-up",
      sub: `The new amount applies from the next charge${next === null ? "" : ` on ${formatTimestamp(next)}`}. No charge today.`,
      cta: "Update auto top-up",
    };
  }

  return {
    title: "Buy QCredits",
    sub: "One balance for every Qubic. Credits never expire, and larger packs cost less per credit.",
    cta: "Buy QCredits",
  };
};

export type CheckoutReturn = "success" | "cancelled";

/** `?checkout=success|cancelled`, the address Stripe Checkout sends people back to. */
export const readCheckoutReturn = (search: string): CheckoutReturn | null => {
  const value = new URLSearchParams(search).get("checkout");

  return value === "success" || value === "cancelled" ? value : null;
};

/** The same query without `checkout`, "" when nothing is left. */
export const stripCheckoutReturn = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete("checkout");
  const rest = params.toString();

  return rest === "" ? "" : `?${rest}`;
};

/** "October 2026" from "2026-10", and the year the month picker groups it under. */
export const monthOption = (month: string): { value: string; label: string; group: string } => {
  const [year, number] = month.split("-");
  const label = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(
    new Date(Date.UTC(Number(year), Number(number) - 1, 1))
  );

  return { value: month, label: `${label} ${year}`, group: year };
};

/** The price list's descriptions. The back office prices the products; the copy lives with the design. */
export const PRODUCT_BLURBS: Record<string, string> = {
  "global-tracking": "Ocean, air and rail shipments with unified milestones and ETAs.",
  "container-tracking": "Container-level tracking with vessel movements and port updates.",
  "air-tracking": "Air waybill milestones across 200+ airlines.",
  "load-calculator": "Container and truck load planning with utilisation figures.",
  "global-freight-rates": "Sea and air freight rates on any lane, compared side by side.",
  "vessel-schedules": "Sailing schedules with departures, transshipments and cut-offs.",
  "transit-time": "Door-to-door transit times from historical shipment data.",
  "freight-booking-platform": "Search, quote and book shipments from multiple carriers.",
  "co2-intelligence": "GLEC-aligned emissions per shipment and reporting.",
  "freight-intelligence": "Market benchmarks, lane trends and demand signals.",
};

/** How many price-list rows show before "Show all". */
export const PRICE_LIST_PREVIEW = 5;

export const FAQ: readonly { q: string; a: string }[] = [
  {
    q: "What can I do with a Qubic?",
    a: "Each Qubic solves a specific task in logistics or international trade, such as tracking a shipment, calculating costs or retrieving data. Choose the products you need and use them when you need them.",
  },
  {
    q: "Where can I use Qubics?",
    a: "Use them on the Qubictron Platform or integrate them into your website, ERP, TMS or other software through an API or widget, where available.",
  },
  {
    q: "How does payment work?",
    a: "Qubics Store is pay as you go. Add QCredits to your balance and pay for the Qubics you use. There is no subscription or minimum usage requirement.",
  },
  {
    q: "How much will I pay?",
    a: "Each Qubic has its own price and billing unit. The price list shows how many QCredits an action costs, whether it's a tracked shipment, a search or a booking.",
  },
  {
    q: "Do QCredits expire?",
    a: "No. Your QCredits never expire. Unused credits remain in your balance until you use them.",
  },
];

/**
 * The header pill's short balance (design/account/RULES.md): always rounded
 * down, at most five characters. 9,999 · 12.4K · 124K · 1.2M · 15M.
 */
export const qcShort = (value: number): string => {
  const n = Math.floor(Number.isFinite(value) ? value : 0);
  const cut = (v: number, digits: number): string => {
    const p = 10 ** digits;

    return String(Math.floor(v * p) / p);
  };

  if (n < 10_000) return formatCount(n);
  if (n < 100_000) return `${cut(n / 1000, 1)}K`;
  if (n < 1_000_000) return `${Math.floor(n / 1000)}K`;
  if (n < 10_000_000) return `${cut(n / 1_000_000, 1)}M`;

  return `${Math.floor(n / 1_000_000)}M`;
};

/** "October" for the dashboard's "184 spent in October". */
const monthName = (): string => new Intl.DateTimeFormat("en-US", { month: "long" }).format(new Date());

/** The dashboard's "QCredits balance" figure and its line underneath. */
export const creditKpi = (state: CreditState): { value: string; hint: string; tone?: "bad" } => {
  const view = billingView(state);
  const value = formatCount(view.empty ? state.balance.free : state.balance.total);

  if (view.empty) {
    return {
      value,
      hint:
        state.balance.free > 0
          ? "Free this month"
          : `Free credits reset on ${formatShortDate(state.balance.free_resets_on)}`,
    };
  }
  if (view.auto?.status === "failed") return { value, hint: "Auto top-up failed", tone: "bad" };
  if (view.auto?.status === "active") {
    return { value, hint: `Auto top-up on ${formatShortTimestamp(view.auto.topUp.next_charge_at)}` };
  }

  return { value, hint: `${formatCount(state.spent_this_month)} spent in ${monthName()}` };
};

const formatShortTimestamp = (value: string | null): string =>
  value === null ? "" : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(value));

export type CreditAttention = {
  id: string;
  tone: "bad" | "plain";
  title: string;
  detail: string;
  action: string;
};

/** Billing lines for "Needs attention": a failing auto top-up first, an upcoming one last. */
export const creditAttention = (state: CreditState): { first: CreditAttention[]; last: CreditAttention[] } => {
  const auto = billingView(state).auto;

  if (auto === null) return { first: [], last: [] };

  const { topUp } = auto;
  const card = cardLabel(topUp.card);

  if (auto.status === "failed") {
    const retry =
      topUp.next_retry_at === null
        ? "no more retries"
        : `Stripe retries on ${formatShortTimestamp(topUp.next_retry_at)}`;

    return {
      first: [
        {
          id: "auto-topup-failed",
          tone: "bad",
          title: "Auto top-up payment failed",
          detail: [card, formatUsd(topUp.amount_usd), retry].filter(Boolean).join(" · "),
          action: "Update card",
        },
      ],
      last: [],
    };
  }

  if (auto.status === "active" && topUp.next_charge_at !== null) {
    return {
      first: [],
      last: [
        {
          id: "auto-topup-next",
          tone: "plain",
          title: `Auto top-up on ${formatShortTimestamp(topUp.next_charge_at)} · ${formatUsd(topUp.amount_usd)}`,
          detail: `${formatCredits(topUp.credits)} will be added to your balance`,
          action: "Plan & Billing",
        },
      ],
    };
  }

  return { first: [], last: [] };
};

/** The out-of-credits prompt's line: what the action needs against what is left. */
export const outOfCreditsText = (denial: Pick<InsufficientCreditsError, "needed" | "balance">): string =>
  `This needs ${formatCredits(denial.needed)} and your balance is ${formatCount(denial.balance)}. The shipments you already follow keep updating.`;
